import type { HelpGuide } from '../types';

export const pettycashGuide: HelpGuide = {
  key: 'pettycash',
  title: 'เงินสดย่อย (Petty Cash)',
  subtitle: 'เมนู: ลูกหนี้ / เจ้าหนี้ ▸ เงินสดย่อย',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/pettycash/คู่มือการใช้งาน_เงินสดย่อย_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “เงินสดย่อย” ใช้คุมเงินสดย่อย (Petty Cash) ของทุกสาขารวมไว้ในที่เดียว ตั้งแต่เปิดวงเงิน (Float) ให้แต่ละสาขา บันทึกค่าใช้จ่ายย่อยรายวัน เติมเงินชดเชย (Replenish) ตรวจนับเงินสด ไปจนถึงปิดวงเงิน — โดยทุกขั้นตอนที่เป็นเงินจะลงบัญชี (JV) ให้อัตโนมัติ และมีการอนุมัติแบบ maker-checker',
        },
        {
          kind: 'callout',
          text: 'หลักการเงินสดย่อย: เปิดวงเงิน → Dr เงินสดย่อย / Cr เงินฝากธนาคาร · บันทึกค่าใช้จ่าย → Dr ค่าใช้จ่าย (+ ภาษีซื้อ) / Cr เงินสดย่อย (หัก ณ ที่จ่ายถ้ามี) · เติมเงินชดเชย → Dr เงินสดย่อย / Cr เงินฝากธนาคาร (เติมยอดที่ใช้ไปคืนให้เต็มวงเงิน) · ปิดวงเงิน → Dr เงินฝากธนาคาร / Cr เงินสดย่อย (คืนเงินสดที่เหลือ)',
        },
        { kind: 'para', text: 'หน้าจอมี 4 แท็บหลัก:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'สถานะรายสาขา — ',
              text: 'ตารางสรุปวงเงินและการใช้จ่ายของทุกสาขา + KPI (แท็บเริ่มต้น)',
            },
            {
              lead: 'สมุดเงินสดย่อยรายสาขา — ',
              text: 'สมุดเงินสดของสาขาที่เลือก แสดงรายการ + ยอดคงเหลือสะสม + กราฟใช้จ่ายรายวัน',
            },
            {
              lead: 'ใบเบิกชดเชย — ',
              text: 'คิวสาขาที่รอเติมเงินชดเชย + ประวัติการชดเชย',
            },
            {
              lead: 'สรุปตามประเภทค่าใช้จ่าย — ',
              text: 'แยกยอดใช้จ่ายตามรหัสบัญชี + 10 สาขาที่ใช้จ่ายมากสุด',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/pettycash/01-overview.png',
          caption:
            'สถานะรายสาขา — KPI (ยอดเบิกจ่ายรวม / วงเงินรวม / สาขาทั้งหมด / รออนุมัติ), ตารางวงเงินรายสาขา และแถบการใช้วงเงิน',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ ด้านบนของหน้า:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ยอดเบิกจ่ายรวม — ',
              text: 'ยอดใช้จ่ายรวมทุกสาขาในงวดที่เลือก พร้อมจำนวนรายการ',
            },
            {
              lead: 'วงเงินรวม (Float) — ',
              text: 'ผลรวมวงเงินของสาขาที่เปิดใช้งาน พร้อม % การใช้วงเงินและยอดคงเหลือ',
            },
            {
              lead: 'สาขาทั้งหมด — ',
              text: 'จำนวนสาขาที่มีวงเงินเปิดใช้งาน และจำนวนสาขาที่ส่งรายงานแล้ว',
            },
            {
              lead: 'รออนุมัติ / เกินกำหนด — ',
              text: 'จำนวนสาขาที่ยังมีรายการรออนุมัติหรือเกินกำหนดส่ง',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มมุมขวาบน: งวด: [เดือน] (เลือกเดือนของข้อมูล ย้อนหลัง 6 เดือน) · นำเข้า (เปิดหน้าต่างนำเข้าวงเงินเงินสดย่อย) · ส่งออก (ดาวน์โหลดวงเงินปัจจุบันเป็น Excel) · บันทึกรายการใหม่ (ปุ่มหลัก เปิดฟอร์มบันทึกรายการเงินสดย่อย)',
        },
        {
          kind: 'para',
          text: 'ตารางสถานะรายสาขากรองได้ด้วย: ค้นหา รหัสสาขา/ชื่อสาขา · เลือกสาขา · สถานะ (ทั้งหมด / ส่งรายงานแล้ว / รออนุมัติ / อนุมัติแล้ว / ชดเชยเงินแล้ว / เกินกำหนด) คอลัมน์: รหัสสาขา · ชื่อสาขา · ผู้รับผิดชอบ · วงเงิน · ใช้ไป · การใช้วงเงิน (แถบสี เขียว/เหลือง/แดง) · รายการเดือนนี้ · ยอดเบิกรวม · ส่งล่าสุด · สถานะ — คลิกที่แถวเพื่อกระโดดไปยังสมุดเงินสดย่อยของสาขานั้น',
        },
      ],
    },
    {
      id: 'cashbook',
      heading: '3. สมุดเงินสดย่อยรายสาขา',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ สมุดเงินสดย่อยรายสาขา (หรือคลิกแถวสาขาในสถานะรายสาขา) แสดงสมุดเงินสดของสาขาที่เลือก ด้านบนมีการ์ดสรุป วงเงินอนุมัติ · ยอดคงเหลือ · ใช้ไป % · คงเหลือ และกราฟแท่งใช้จ่ายรายวันตลอดเดือน (แต่ละแท่งมีสีตาม % การใช้วงเงิน)',
        },
        {
          kind: 'para',
          text: 'ตารางรายการแสดงคอลัมน์: เลขที่สำคัญ · วันที่ · ประเภท (รหัสบัญชี + ชื่อบัญชี) · รายละเอียด · จำนวนเงิน · ยอดคงเหลือ (สะสมหลังหักรายการ) · ส่งแล้ว (สถานะการส่งอนุมัติ) · ใบเสร็จ (ไม่มีใบเสร็จ / ไม่มีเอกสาร / มีแล้ว) · การกระทำ กรองด้วย ค้นหาตามรายละเอียด/เลขที่สำคัญ และตัวกรองใบเสร็จ (ทั้งหมด / มีใบเสร็จ / ไม่มีใบเสร็จ) มีปุ่ม ส่งออก สำหรับดาวน์โหลดรายการที่กรองเป็น Excel',
        },
        {
          kind: 'image',
          src: '/help/pettycash/02-entries-cashbook.png',
          caption:
            'สมุดเงินสดย่อยรายสาขา — รายการ + ยอดคงเหลือสะสม กราฟใช้จ่ายรายวัน และสถานะของแต่ละรายการ',
        },
      ],
    },
    {
      id: 'entry-create',
      heading: '4. การบันทึกรายการเงินสดย่อย',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม บันทึกรายการใหม่ แล้วกรอก: วงเงินที่เลือก (สาขา) · วันที่ทำรายการ · รายละเอียด · ประเภทค่าใช้จ่าย (เลือกรหัสบัญชีค่าใช้จ่าย) · หน่วยงาน (Cost Center) (ไม่บังคับ) · จำนวนเงิน · ผู้รับเงิน · เลขประจำตัวผู้เสียภาษี · เลขที่ใบเสร็จ',
        },
        {
          kind: 'callout',
          text: 'ต้องแนบใบเสร็จ หรือ ติ๊ก “ไม่มีเอกสาร” อย่างใดอย่างหนึ่งเสมอ — ระบบจะไม่ยอมบันทึกรายการที่ไม่มีหลักฐานและไม่ได้ระบุว่าไม่มีเอกสาร',
        },
        {
          kind: 'para',
          text: 'ส่วนภาษีซื้อ (ไม่บังคับ): เลือกการปฏิบัติทางภาษี CLAIMABLE (ขอคืนได้) / SUSPENSE (พักไว้รอใบกำกับ) / PROHIBITED (ขอคืนไม่ได้) พร้อมระบุ อัตราภาษี (เริ่มต้น 7%) · จำนวนภาษี (คำนวณให้อัตโนมัติ แก้ได้) · เลขที่/วันที่ใบกำกับภาษี ส่วนหัก ณ ที่จ่าย (ไม่บังคับ): จำนวนหักภาษี ณ ที่จ่าย · อัตราหักภาษี · ประเภทรายได้ (ภ.ง.ด.) ระบบจะแสดงพรีวิว ค่าใช้จ่ายสุทธิ · ภาษีซื้อ · หัก ณ ที่จ่าย · เงินสดจ่ายจริง และ คงเหลือหลังบันทึก แบบเรียลไทม์',
        },
        {
          kind: 'image',
          src: '/help/pettycash/06-entry-create.png',
          caption:
            'บันทึกรายการเงินสดย่อย — ฟอร์ม พร้อมส่วนภาษีซื้อ (Claimable) หัก ณ ที่จ่าย และพรีวิวยอดก่อน-หลัง VAT',
        },
      ],
    },
    {
      id: 'entry-approval',
      heading: '5. สายอนุมัติเงินสดย่อย (PCV)',
      blocks: [
        {
          kind: 'para',
          text: 'รายการที่ต้องผ่านการอนุมัติจะมีสถานะ “รออนุมัติ” คลิกที่ป้ายอนุมัติบนแถวรายการเพื่อเปิดแผงสายอนุมัติ ระบบใช้กลไก maker-checker กลางตัวเดียวกับเอกสารอื่น ๆ แสดงไทม์ไลน์การอนุมัติทีละขั้น (ผู้บันทึก → ผู้ตรวจ → ผู้อนุมัติ) พร้อมผู้ทำ เวลา และความเห็นของแต่ละขั้น',
        },
        {
          kind: 'para',
          text: 'ปุ่มที่ใช้ได้ขึ้นกับสิทธิ์และสถานะ: ส่งอนุมัติ (สำหรับรายการที่ยังเป็นฉบับร่าง/ถูกส่งกลับ) · อนุมัติ / ส่งกลับ / ปฏิเสธ (สำหรับผู้อนุมัติ) เมื่อกดจะเปิดหน้าต่างตัดสินใจให้ใส่ความเห็น (ส่งกลับ/ปฏิเสธ ต้องระบุเหตุผล) และเหตุผลกรณีข้ามข้อจำกัดผู้ทำ-ผู้อนุมัติคนเดียวกัน (SoD) เมื่ออนุมัติขั้นสุดท้าย ระบบจะลงบัญชี (JV) ให้อัตโนมัติ และเปลี่ยนสถานะเป็น “บันทึกแล้ว”',
        },
        {
          kind: 'image',
          src: '/help/pettycash/03-entry-approval.png',
          caption:
            'สายอนุมัติเงินสดย่อย (PCV) — ไทม์ไลน์ maker-checker พร้อมปุ่มอนุมัติ / ส่งกลับ / ปฏิเสธ',
        },
      ],
    },
    {
      id: 'cash-count',
      heading: '6. การตรวจนับเงินสด (PCC)',
      blocks: [
        {
          kind: 'para',
          text: 'ใช้บันทึกการตรวจนับเงินสดจริงเทียบกับยอดตามบัญชี ที่สมุดเงินสดย่อยกดปุ่ม นับเงินสด แล้วกรอก: วงเงินที่เลือก · วันที่ตรวจนับ · ยอดเงินที่ตรวจนับได้ · เหตุผล (ถ้ามี) ระบบแสดงพรีวิว ยอดคาดว่า (ตามบัญชี) · ส่วนต่าง (นับจริง − บัญชี) โดยมีสีบอกผล: เขียว (ตรงพอดี) เหลือง (ต่างเล็กน้อย) แดง (ขาด/เกินมาก)',
        },
        {
          kind: 'para',
          text: 'เมื่อกด จดทะเบียน รายการตรวจนับจะเข้าสู่สายอนุมัติ (1 ผู้อนุมัติ) เมื่ออนุมัติขั้นสุดท้าย ระบบลงใบสำคัญผลต่างขาด/เกิน (over/short) ให้อัตโนมัติ โดยไม่กระทบยอดวงเงิน',
        },
        {
          kind: 'image',
          src: '/help/pettycash/04-cash-count.png',
          caption: 'นับเงินสด (PCC) — เทียบยอดนับจริงกับยอดตามบัญชี แสดงผลต่าง (ขาด/เกิน)',
        },
      ],
    },
    {
      id: 'float-close',
      heading: '7. การปิดวงเงิน (Float) (PCX)',
      blocks: [
        {
          kind: 'para',
          text: 'ใช้เมื่อต้องการยกเลิกวงเงินเงินสดย่อยของสาขาและคืนเงินสดที่เหลือกลับเข้าธนาคาร ที่สมุดเงินสดย่อยกดปุ่ม ปิด float แล้วกรอก: วงเงินที่เลือก · วันที่ปิด · บัญชีธนาคารที่ส่งคืน · จำนวนเงินคืน (เริ่มต้นเท่ายอดคงเหลือ แก้ได้) · หมายเหตุ',
        },
        {
          kind: 'para',
          text: 'เมื่อกด ปิดวงเงิน คำขอจะเข้าสู่สายอนุมัติ 2 ขั้น เมื่ออนุมัติครบ ระบบลงใบสำคัญคืนเงิน (Dr เงินฝากธนาคาร / Cr เงินสดย่อย) และเปลี่ยนสถานะวงเงินเป็น “ปิดแล้ว” ซึ่งจะไม่รับรายการใหม่อีก',
        },
        {
          kind: 'image',
          src: '/help/pettycash/05-float-close.png',
          caption: 'ปิด Float (PCX) — เลือกบัญชีคืนเงิน ระบุยอดคืน และยื่นขอปิดวงเงิน',
        },
      ],
    },
    {
      id: 'replen',
      heading: '8. ใบเบิกชดเชย (Replenishment)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ ใบเบิกชดเชย ใช้เติมเงินคืนวงเงินที่ใช้ไปให้เต็มอีกครั้ง ด้านบนมี KPI 3 ใบ: รอชดเชย (มียอดใช้ไป) · วงเงินใกล้หมด (>90% — ควรเร่งชดเชย) · ใบเบิกชดเชยทั้งหมด ถัดมาเป็นคิวสาขา (เรียงตาม % การใช้วงเงินจากมากไปน้อย) แสดง สาขา · ผู้รับผิดชอบ · วงเงิน · ใช้ไป · การใช้วงเงิน และปุ่ม ชดเชย',
        },
        {
          kind: 'para',
          text: 'กดปุ่ม ชดเชย (หรือปุ่มลอย เติมเงิน มุมขวาล่าง) จะเปิดฟอร์มเบิกชดเชย ให้เลือก วงเงินที่เลือก · วันที่เบิกชดเชย · บัญชีธนาคารที่เบิก ระบบแสดงพรีวิว จำนวนเบิก (= วงเงิน − ยอดคงเหลือ) เมื่อยืนยัน ระบบรวมรายการที่บันทึกแล้วทั้งหมดของวงเงิน ลงใบสำคัญเติมเงิน (Dr เงินสดย่อย / Cr ธนาคาร) และเพิ่มยอดคงเหลือกลับเต็มวงเงิน ด้านล่างมีประวัติการชดเชย (เลขที่สำคัญ · สาขา · วันที่ · จำนวนเงิน · ผู้บัญชี)',
        },
        {
          kind: 'image',
          src: '/help/pettycash/07-replen-queue.png',
          caption:
            'ใบเบิกชดเชย — KPI สาขาที่รอชดเชย คิวสาขาเรียงตามการใช้วงเงิน และประวัติการชดเชย',
        },
      ],
    },
    {
      id: 'category',
      heading: '9. สรุปตามประเภทค่าใช้จ่าย',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ สรุปตามประเภทค่าใช้จ่าย แยกยอดใช้จ่ายในงวดตามรหัสบัญชี (COA) แสดงคอลัมน์: รหัส · ประเภทค่าใช้จ่าย (ชื่อบัญชี + จุดสี) · สัดส่วน (แถบเทียบกับยอดสูงสุด) · จำนวนรายการ · มูลค่ารวม · เฉลี่ย/ใบ · % ของรวม ช่วยให้เห็นว่าเงินสดย่อยถูกใช้ไปกับค่าใช้จ่ายประเภทใดมากที่สุด',
        },
        {
          kind: 'para',
          text: 'แผงด้านขวาแสดง 10 สาขาที่ใช้จ่ายมากสุด เพื่อช่วยติดตามสาขาที่มีการใช้เงินสดย่อยสูง',
        },
        {
          kind: 'image',
          src: '/help/pettycash/08-category.png',
          caption: 'สรุปตามประเภทค่าใช้จ่าย — แยกตามรหัสบัญชี และ 10 สาขาที่ใช้จ่ายมากสุด',
        },
      ],
    },
    {
      id: 'float-import',
      heading: '10. การนำเข้าวงเงินเงินสดย่อย',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม นำเข้า ที่หัวหน้าจอเพื่อเปิดวงเงินให้หลายสาขาพร้อมกันจากไฟล์ Excel โดย หนึ่งแถว = เปิดวงเงินหนึ่งสาขา ดาวน์โหลดเทมเพลตในหน้าต่าง แล้วกรอกคอลัมน์: รหัสสาขา (บังคับ) · วงเงิน (Float) (บังคับ ต้องมากกว่า 0) · วันที่เปิด (ไม่ระบุ = วันนี้) · รหัสพนักงานผู้รับผิดชอบ (ไม่บังคับ)',
        },
        {
          kind: 'para',
          text: 'หลังอัปโหลด ระบบแสดงตารางพรีวิว: สาขา · วงเงิน · วันที่เปิด · ผู้รับผิดชอบ พร้อมตรวจสอบรหัสสาขาและรหัสพนักงานก่อนยืนยันนำเข้า นอกจากนี้ยังนำเข้ารายการเงินสดย่อยย้อนหลังได้ (หนึ่งแถว = หนึ่งรายการ เข้าวงเงินที่เปิดใช้งานของสาขา และถูกตั้งเป็น “ไม่แนบใบเสร็จ” อัตโนมัติ สำหรับบันทึกย้อนหลังจากเอกสารกระดาษ)',
        },
        {
          kind: 'image',
          src: '/help/pettycash/09-float-import.png',
          caption: 'นำเข้าวงเงินเงินสดย่อย — คอลัมน์ในตาราง เทมเพลต และพรีวิวก่อนยืนยัน',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '11. สรุปสถานะ',
      blocks: [
        { kind: 'para', text: 'สถานะของรายการเงินสดย่อย:' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['บันทึกแล้ว (POSTED)', 'อนุมัติครบ ลงบัญชีแล้ว หักเงินออกจากวงเงิน'],
            ['ชดเชยแล้ว (REPLENISHED)', 'รายการถูกรวมเข้าใบเบิกชดเชย วงเงินถูกเติมคืนแล้ว'],
            ['รออนุมัติ (PENDING_APPROVAL)', 'ส่งแล้ว รอผู้อนุมัติขั้นถัดไป'],
            ['ปฏิเสธ (REJECTED)', 'ถูกปฏิเสธในขั้นอนุมัติ'],
          ],
        },
        { kind: 'para', text: 'สถานะของวงเงิน (Float) และการตรวจนับ/ปิดวงเงิน:' },
        {
          kind: 'table',
          head: ['สถานะ', 'ความหมาย'],
          rows: [
            ['เปิดใช้งาน (ACTIVE)', 'วงเงินยังใช้งานได้ รับรายการใหม่ได้'],
            ['ปิดแล้ว (CLOSED)', 'วงเงินถูกปิด ไม่รับรายการใหม่'],
            ['รออนุมัติ (PENDING_APPROVAL)', 'การตรวจนับ/ปิดวงเงิน รอการอนุมัติ'],
            ['บันทึกแล้ว (POSTED)', 'อนุมัติครบ ลงบัญชีแล้ว'],
            ['ปฏิเสธ (REJECTED)', 'การตรวจนับ/ปิดวงเงิน ถูกปฏิเสธ'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '12. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'บันทึกรายการแล้วระบบไม่ยอมให้บันทึก?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ทุกรายการต้องแนบใบเสร็จ หรือ ติ๊ก “ไม่มีเอกสาร” อย่างใดอย่างหนึ่ง และจำนวนเงินต้องมากกว่า 0',
            },
          ],
        },
        { kind: 'para', text: 'เติมเงินชดเชยแล้ววงเงินกลับมาเต็มได้อย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ใบเบิกชดเชยจะรวมรายการที่บันทึกแล้วทั้งหมดของวงเงิน ลงใบสำคัญเติมเงินจากธนาคารคืนวงเงิน (Dr เงินสดย่อย / Cr ธนาคาร) ยอดคงเหลือจึงกลับเต็มวงเงิน',
            },
          ],
        },
        { kind: 'para', text: 'ภาษีซื้อแบบ SUSPENSE (พักไว้) ต่างจาก CLAIMABLE อย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'SUSPENSE ใช้เมื่อยังไม่ได้ใบกำกับภาษีตัวจริง เมื่อได้ใบกำกับแล้วให้กด “ยืนยันภาษีซื้อ” ที่รายการ ระบบจะโอนจากภาษีพักเป็นภาษีซื้อขอคืนได้',
            },
          ],
        },
        { kind: 'para', text: 'ตรวจนับเงินสดแล้วยอดไม่ตรง?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ระบบแสดงส่วนต่าง (นับจริง − บัญชี) ให้ และเมื่ออนุมัติการตรวจนับจะลงใบสำคัญผลต่างขาด/เกินให้อัตโนมัติ โดยไม่กระทบยอดวงเงิน',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '13. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'วงเงิน (Float) — ',
              text: 'จำนวนเงินสดย่อยที่กำหนดให้แต่ละสาขาถือไว้ใช้จ่าย',
            },
            {
              lead: 'ผู้รับผิดชอบ (Custodian) — ',
              text: 'พนักงานที่ดูแลและรับผิดชอบเงินสดย่อยของสาขา',
            },
            {
              lead: 'ใบเบิกชดเชย (Replenishment) — ',
              text: 'การเติมเงินจากธนาคารคืนวงเงินตามยอดที่ใช้ไป ให้กลับเต็มวงเงิน',
            },
            {
              lead: 'ตรวจนับเงินสด (PCC) — ',
              text: 'การนับเงินสดจริงเทียบกับยอดตามบัญชี เพื่อหาผลต่างขาด/เกิน',
            },
            {
              lead: 'ปิดวงเงิน (PCX) — ',
              text: 'การยกเลิกวงเงินของสาขาและคืนเงินสดที่เหลือกลับเข้าธนาคาร',
            },
            {
              lead: 'ภาษีซื้อ (Input VAT) — ',
              text: 'ภาษีมูลค่าเพิ่มที่จ่ายตอนซื้อ — CLAIMABLE ขอคืนได้ / SUSPENSE พักรอใบกำกับ / PROHIBITED ขอคืนไม่ได้',
            },
            {
              lead: 'หัก ณ ที่จ่าย (WHT) — ',
              text: 'ภาษีหัก ณ ที่จ่ายที่หักจากผู้รับเงิน ออกหนังสือรับรอง 50 ทวิ (ภ.ง.ด. 3/53)',
            },
            {
              lead: 'maker-checker — ',
              text: 'การแบ่งหน้าที่ผู้บันทึกกับผู้อนุมัติคนละคน (SoD) เพื่อควบคุมภายใน',
            },
          ],
        },
      ],
    },
  ],
};
